import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import linkedinIcon from "../assets/images/social/linkedin.svg";
import xIcon from "../assets/images/social/x-twitter.svg";
import instagramIcon from "../assets/images/social/instagram.svg";
import facebookIcon from "../assets/images/social/facebook.svg";
import threadsIcon from "../assets/images/social/threads.svg";
import emailIcon from "../assets/images/social/email.svg";
import {
  AccountInsights,
  ConnectedPlatform,
  Platform,
} from "../types/social-connect";
import { usePfmSocialConnectApi } from "../api/use-pfm-social-connect-api";
import { BrandDetails } from "../types/add-brand-types";
import { isNotEmpty } from "../utils/type-utils";
import { useTimeUtil } from "./useTimeUtil";

export const usePfmSocialConnections = (
  brandDetails: BrandDetails,
  refreshBrandDetails?: () => void
) => {
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);
  const {
    createProfilePfm,
    connectPlatformPfm,
    getConnectedPlatformsPfm,
    disconnectPlatformPfm,
    getAccountInsightsPfm,
  } = usePfmSocialConnectApi();
  const [socialProfileConnected, setSocialProfileConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] =
    useState<ConnectedPlatform[]>();
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedManagePlatform, setSelectedManagePlatform] =
    useState<Platform | null>(null);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [selectedInsightsPlatform, setSelectedInsightsPlatform] =
    useState<Platform | null>(null);
  const [insightsData, setInsightsData] = useState<AccountInsights | null>(
    null
  );
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const { formatIso } = useTimeUtil();
  const refreshBrandDetailsRef = useRef(refreshBrandDetails);
  refreshBrandDetailsRef.current = refreshBrandDetails;

  useEffect(() => {
    const activated = isNotEmpty(brandDetails.post_for_me_profile);
    setSocialProfileConnected(activated);
    const cachedPlatforms = (brandDetails.pfm_social_profile_ids?.accounts ??
      []) as unknown as ConnectedPlatform[];

    if (!activated) {
      setIsLoading(false);
      return;
    }

    const getConnectedSocials = async () => {
      setIsLoading(true);
      if (cachedPlatforms.length > 0) {
        setConnectedPlatforms(cachedPlatforms.filter((p) => p.isActive));
      }
      const platforms = await getConnectedPlatformsPfm(brandDetails.id);
      if (platforms.length > 0) {
        setConnectedPlatforms(platforms.filter((p) => p.isActive));
      } else if (cachedPlatforms.length > 0) {
        setConnectedPlatforms(cachedPlatforms.filter((p) => p.isActive));
      } else {
        setConnectedPlatforms([]);
      }
      setIsLoading(false);
    };
    getConnectedSocials();
  }, [
    brandDetails.id,
    brandDetails.post_for_me_profile,
    brandDetails.pfm_social_profile_ids,
    getConnectedPlatformsPfm,
  ]);

  const handleManage = useCallback((platform: Platform) => {
    setSelectedManagePlatform(platform);
    setIsManageModalOpen(true);
  }, []);

  const closeManageModal = useCallback(() => {
    setIsManageModalOpen(false);
    setSelectedManagePlatform(null);
  }, []);

  const handleInsights = useCallback(
    async (platform: Platform) => {
      console.log(
        "[Insights] Platform:",
        platform.name,
        "accountId:",
        platform.accountId
      );
      setSelectedInsightsPlatform(platform);
      setInsightsData(null);
      setIsInsightsModalOpen(true);
      setIsInsightsLoading(true);

      if (platform.accountId) {
        const data = await getAccountInsightsPfm(platform.accountId);
        setInsightsData(data ?? null);
      } else {
        console.warn(
          "[Insights] No accountId found for platform:",
          platform.name
        );
      }

      setIsInsightsLoading(false);
    },
    [getAccountInsightsPfm]
  );

  const closeInsightsModal = useCallback(() => {
    setIsInsightsModalOpen(false);
    setSelectedInsightsPlatform(null);
    setInsightsData(null);
  }, []);

  const handleDisconnect = useCallback(async () => {
    if (!selectedManagePlatform?.accountId) {
      setToast({
        message: "Unable to disconnect: account id not found.",
        type: "error",
        isVisible: true,
      });
      return;
    }

    setIsDisconnecting(true);
    const ok = await disconnectPlatformPfm(
      selectedManagePlatform.accountId,
      brandDetails.id
    );

    if (ok) {
      setConnectedPlatforms((prev) =>
        (prev ?? []).filter(
          (p: any) => p._id !== selectedManagePlatform.accountId
        )
      );
      setToast({
        message: `${selectedManagePlatform.name} disconnected successfully.`,
        type: "success",
        isVisible: true,
      });
      closeManageModal();
      refreshBrandDetailsRef.current?.();
    } else {
      setToast({
        message: `Failed to disconnect ${selectedManagePlatform.name}.`,
        type: "error",
        isVisible: true,
      });
    }

    setIsDisconnecting(false);
  }, [
    brandDetails.id,
    closeManageModal,
    disconnectPlatformPfm,
    selectedManagePlatform,
  ]);

  const platforms: Platform[] = useMemo(() => {
    let linkedInData: ConnectedPlatform | undefined = undefined;
    let twitterData: ConnectedPlatform | null = null;
    let instagramData: ConnectedPlatform | null = null;
    let facebookData: ConnectedPlatform | null = null;
    let threadsData: ConnectedPlatform | null = null;

    if (isNotEmpty(connectedPlatforms)) {
      connectedPlatforms.forEach((platform) => {
        const p = (platform.platform ?? "").toLowerCase();
        if (p === "linkedin") {
          linkedInData = platform;
        } else if (p === "twitter" || p === "x") {
          twitterData = platform;
        } else if (p === "instagram") {
          instagramData = platform;
        } else if (p === "facebook") {
          facebookData = platform;
        } else if (p === "threads") {
          threadsData = platform;
        }
      });
    }

    return [
      {
        id: "linkedin",
        name: "LinkedIn Company Page",
        description:
          "Manage your professional presence and publish updates to your company followers.",
        icon: linkedinIcon,
        status: isNotEmpty(linkedInData) ? "connected" : "disconnected",
        lastSynced: isNotEmpty(linkedInData)
          ? formatIso(linkedInData!.updatedAt)
          : "---",
        type: "platform",
        username: isNotEmpty(linkedInData) ? linkedInData!.username : undefined,
        profilePicture: isNotEmpty(linkedInData)
          ? linkedInData!.profilePicture
          : undefined,
        accountId: isNotEmpty(linkedInData)
          ? (linkedInData as any)!._id
          : undefined,
      },
      {
        id: "x",
        name: "X (Twitter)",
        description:
          "Post updates, threads, and engage with your community in real-time.",
        icon: xIcon,
        status: isNotEmpty(twitterData) ? "connected" : "disconnected",
        lastSynced: isNotEmpty(twitterData)
          ? formatIso(twitterData!.updatedAt)
          : "---",
        type: "platform",
        username: isNotEmpty(twitterData) ? twitterData!.username : undefined,
        profilePicture: isNotEmpty(twitterData)
          ? twitterData!.profilePicture
          : undefined,
        accountId: isNotEmpty(twitterData)
          ? (twitterData as any)!._id
          : undefined,
      },
      {
        id: "threads",
        name: "Threads",
        description:
          "Publish short-form updates and monitor engagement across your Threads audience.",
        icon: threadsIcon,
        status: isNotEmpty(threadsData) ? "connected" : "disconnected",
        lastSynced: isNotEmpty(threadsData)
          ? formatIso(threadsData!.updatedAt)
          : "---",
        type: "platform",
        username: isNotEmpty(threadsData) ? threadsData!.username : undefined,
        profilePicture: isNotEmpty(threadsData)
          ? threadsData!.profilePicture
          : undefined,
        accountId: isNotEmpty(threadsData)
          ? (threadsData as any)!._id
          : undefined,
      },
      {
        id: "instagram",
        name: "Instagram Business",
        description: "Post images and reels directly and access analytics.",
        icon: instagramIcon,
        status: isNotEmpty(instagramData) ? "connected" : "disconnected",
        lastSynced: isNotEmpty(instagramData)
          ? formatIso(instagramData!.updatedAt)
          : "---",
        type: "platform",
        username: isNotEmpty(instagramData)
          ? instagramData!.username
          : undefined,
        profilePicture: isNotEmpty(instagramData)
          ? instagramData!.profilePicture
          : undefined,
        accountId: isNotEmpty(instagramData)
          ? (instagramData as any)!._id
          : undefined,
      },
      {
        id: "facebook",
        name: "Facebook Page",
        description:
          "Share updates to your page and manage comments efficiently.",
        icon: facebookIcon,
        status: isNotEmpty(facebookData) ? "connected" : "disconnected",
        lastSynced: isNotEmpty(facebookData)
          ? formatIso(facebookData!.updatedAt)
          : "---",
        type: "platform",
        username: isNotEmpty(facebookData) ? facebookData!.username : undefined,
        profilePicture: isNotEmpty(facebookData)
          ? facebookData!.profilePicture
          : undefined,
        accountId: isNotEmpty(facebookData)
          ? (facebookData as any)!._id
          : undefined,
      },
      // Reddit is intentionally hidden in the connections UI until the flow is supported end to end.
    ];
  }, [connectedPlatforms, formatIso]);

  const otherChannels: Platform[] = useMemo(
    () => [
      {
        id: "email",
        name: "Resend / Email API",
        description: "Send automated emails and newsletters.",
        icon: emailIcon,
        status: "connected" as const,
        type: "other" as const,
      },
    ],
    []
  );

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  const handleConnect = useCallback(
    async (platformName: string) => {
      setIsLoading(true);
      const connectionResponse = await connectPlatformPfm(
        platformName,
        brandDetails.id
      );
      setIsLoading(false);

      if (isNotEmpty(connectionResponse)) {
        const authUrl = connectionResponse?.data?.authUrl;
        if (isNotEmpty(authUrl)) {
          window.open(authUrl, "_blank");
        }
      } else {
        setToast({
          message: `Failed to connect ${platformName}!`,
          type: "error",
          isVisible: true,
        });
      }

      setTimeout(() => {
        setToast(null);
      }, 3500);
    },
    [brandDetails.id, connectPlatformPfm]
  );

  const handleCreateProfile = useCallback(async () => {
    setIsLoading(true);
    const socialProfile = await createProfilePfm(
      brandDetails.id,
      brandDetails.brand_name
    );

    if (isNotEmpty(socialProfile)) {
      setSocialProfileConnected(true);
      setToast({
        message: "Social profile created successfully!",
        type: "success",
        isVisible: true,
      });
    } else {
      setToast({
        message: "Failed to create social profile!",
        type: "error",
        isVisible: true,
      });
    }
    setIsLoading(false);
  }, [brandDetails.brand_name, brandDetails.id, createProfilePfm]);

  return {
    handleManage,
    handleConnect,
    handleToastClose,
    otherChannels,
    platforms,
    toast,
    socialProfileConnected,
    handleCreateProfile,
    isLoading,
    isManageModalOpen,
    selectedManagePlatform,
    closeManageModal,
    handleDisconnect,
    isDisconnecting,
    handleInsights,
    isInsightsModalOpen,
    selectedInsightsPlatform,
    insightsData,
    isInsightsLoading,
    closeInsightsModal,
  };
};
